import cookie from 'js-cookie';
import get from 'lodash/get';

import {
  bugsnagError
} from 'services/bugsnag';
import * as request from 'utils/request';
import ApiConfig, {
  PRODUCT_SEARCH_API_URL,
  WAREHOUSE_API_URL
} from 'config/endpoints';
import { memoizeAsync } from 'utils/utils';
import isInteger from 'lodash/isInteger';

// ==============================================================================
/**
 * Warehouse APIs.
 *
 * Goal is to consolidate all of the warehouse API calls inside the service.
 *
 * Error handling:
 * The API calls should throw an Error object from Promise on error.
 * This allows the error to be passed up and handled in calling thunk
 * or even in componentDidMount. TODO: bugsnag integration here for server errors.
 */
// ==============================================================================

/**
 * Response error handling helper.
 * Checks for an error field on the response,
 * if found bugsnags the error and then throws.
 */
export const throwOnResponseError = (response, url) => {
  if (response && response.error) {
    // we want the status code too so stringify the whole response
    const error = new Error(JSON.stringify(response));
    bugsnagError(error, {
      url
    });
    throw new Error(error);
  }
};

/**
 * Response error handling helper.
 * Bugsnags error and then throws it.
 */
export const throwError = (error, data) => {
  bugsnagError(error, {
    data
  });
  throw error;
};

/**
 * Log out the logged in user.
 */
export const logoutUser = () => {
  // Update server so token can be invalidated.
  const url = `${WAREHOUSE_API_URL}/warehouse/api/logout`;
  return request.get(url)
    .then(() => {})
    .catch(error => {
      bugsnagError(error, {
        url
      });
    });
};

/**
 * Submit user feedback for a design.
 */
export const submitDesignFeedback = ({
  designId,
  spaceId,
  userFeedback
}) => {
  const postData = {
    rating: userFeedback.rating, // number
    areas: userFeedback.areas, // array of strings
    comments: userFeedback.comments // string
  };

  const url = `${WAREHOUSE_API_URL}/warehouse/studio/spaces/${spaceId}/designs/${designId}/designfeedback`;
  return request.post(
    url,
    postData
  ).then(response => {
    throwOnResponseError(response, url);
  });
};

/**
 * Submit user feedback for a design V2.
 * We need to POST if feedback is not already existing.
 * Otherwise, we need to PATCH.
 */
export const submitDesignFeedbackV2 = ({
  design,
  userFeedback
}) => {
  let postData = {
    ...userFeedback
  };

  const id = design.userFeedbackId();
  const updateMethod = id ? request.patch : request.post;
  if (!id) {
    postData.design = design.id();
  }

  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designfeedbacks${id ? `/${id}` : ''}`;
  return updateMethod(
    url,
    postData
  ).then(response => {
    throwOnResponseError(response, url);
    return response.data;
  });
};

/**
 * Product search.
 */
export const productSearch = (data) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/products/search?pdp=true`;

  return request.post(url, data)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

export const variantProducts = (productId, partnerSlug) => {
  if (!productId) {
    throw new Error('variantProducts requires a product ID', productId);
  }
  let url = `${WAREHOUSE_API_URL}/warehouse/api/products?id=${productId}&app=studio&format=json&mode=variants&exclude_original_product=true`;

  if (partnerSlug) {
    url += '&partner=' + partnerSlug;
  }

  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Product catalog search.
 */
export const productCatalogSearchV2 = ({
  // the search query string
  searchQuery = '',

  // [optional] additional search query parameters
  queryParams = '',

  // for prev and next, server returns the full request URL
  // including hostname and all filter params. If provided,
  // we use this one directly.
  requestUrl = ''
} = {}) => {
  let url = requestUrl;
  if (!url) {
    url = `${WAREHOUSE_API_URL}/warehouse/api/v2/search/shop?out_of_stock=false&page_size=48`;
    if (searchQuery) { url += `&search=${encodeURIComponent(searchQuery)}`; }
    if (queryParams) { url += queryParams; }
  }

  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Products Purchased
 * gets all products purchased for specified userId
 */
export const getProductsPurchased = (userId) => {
  let url = `${WAREHOUSE_API_URL}/warehouse/api/payment/orders/products`;

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Gets products for a space optionally filtered by designIndex.
 */
export const getSpaceProducts = ({
  spaceId,
  designIndex,
  shared
}) => {
  let url = `${WAREHOUSE_API_URL}/warehouse/api/v2/spaceproducts/${spaceId}${shared ? '/shared' : ''}`;

  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);

      let styleboardProducts = get(response, 'data.products', []);
      if (designIndex) {
        styleboardProducts = styleboardProducts.filter(styleboardProduct => {
          return styleboardProduct.design_indexes.includes(designIndex);
        });
      }

      return styleboardProducts.map(p => p.product);
    });
};

/**
 * Bessie Link
 * Gets the link necessary for user to visit 3D Editor
 */
export const getBessieLink = (spaceId, designIndex) => {
  let url = `
    ${WAREHOUSE_API_URL}/warehouse/api/v1/spaces/${spaceId}/designs/${designIndex}/start-bessie
  `;

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);

      // TODO: Add this param on the www side of things
      return `${response.data.redirect}&origDesign=${designIndex}`;
    });
};

/**
 * Simply gets a product from the BE using its id
 */
export const getProductById = (productId) => {
  const url = `${PRODUCT_SEARCH_API_URL}/warehouse/api/products/${productId}`;

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Send post request for redesign assistance
 */
export const sendAssistanceRequest = ({
  spaceId,
  assistanceRequest
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/send_assistance`;
  return request.post(url, {
    ...assistanceRequest
  }).then(response => {
    throwOnResponseError(response, url);
    return response.data;
  });
};

/**
 * Request studio space list for a user ID.
 */
export const getStudioSpaceListFor = ({
  page,
  pageSize,
  userId
}) => {
  let url = `${WAREHOUSE_API_URL}/warehouse/api/v2/spaces?filter=brief&status=pp&userId=${userId}`;
  if (pageSize) {
    url += `&pageSize=${pageSize}`;
  }
  if (page) {
    url += `&page=${page}`;
  }

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Request all studio spaces for a user ID.
 */
export const getStudioSpacesFor = ({
  pageSize,
  userId
}) => {
  let url = `${WAREHOUSE_API_URL}/warehouse/api/v2/spaces?userId=${userId}`;
  if (pageSize) {
    url += `&pageSize=${pageSize}`;
  }

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Request space by spaceId
 */
export const getSpace = ({
  spaceId
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/spaces/${spaceId}`;

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Request coupon for a user ID.
 */
export const fetchCoupon = (userId) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/get_user_coupons?user_id=${userId}`;
  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Gets all promos from retailers.
 */
export const fetchAllPromos = () => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/sourcediscounts`;

  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Track space visits.
 */
const spaceVisit = ({
  spaceId,
  owner,
  shared
}) => {
  const postData = owner ? {
    'view_owner': 1
  } : {
    'view_share': 1
  };

  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/space_view/${spaceId}`;

  return request.post(url, postData)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    })
    .catch(error => {
      // Ignore errors for this API call.
      bugsnagError(error, {
        url,
        postData
      });
    });
};

export const spaceVisitOwner = (spaceId) => {
  spaceVisit({
    spaceId,
    owner: true
  });
};

export const spaceVisitShared = (spaceId) => {
  spaceVisit({
    spaceId,
    shared: true
  });
};

/**
 * Track design visits.
 */
const designVisit = ({
  designId,
  owner,
  shared
}) => {
  const postData = owner ? {
    'view_owner': 1
  } : {
    'view_share': 1
  };

  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/design_view/${designId}`;

  return request.post(url, postData)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    })
    .catch(error => {
      // Ignore errors for this API call.
      bugsnagError(error, {
        url,
        postData
      });
    });
};

export const designVisitOwner = (designId) => {
  designVisit({
    designId,
    owner: true
  });
};

export const designVisitShared = (designId) => {
  designVisit({
    designId,
    shared: true
  });
};

/*
 * Remove an uploaded user photo.
 */
export const removePhoto = ({
  photoId,
  spaceId
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/dashboard/remove-photos`;
  const postData = {
    idList: [photoId]
  };

  return request.post(url, postData)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Get Retailer Logo
 */
export const getRetailerLogo = ({
  slug
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/partners/${slug}/logo`;
  return request.get(url).then(
    response => {
      throwOnResponseError(response, url);
      return response.data;
    }
  );
};

/**
 * Get Retailer/Modsy's Referral Questions
 */
export const getReferralQA = ({
  slug
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/partner_referral_question${slug ? '/' + slug : ''}`;
  return request.get(url).then(
    response => {
      throwOnResponseError(response, url);
      return response.data;
    }
  );
};

/**
 * Fetches or Updates the logged in user's saved shopping cart.
 */
const processCartResponse = (response, { segment = 'merchandise' } = {}) => {
  if (response.status !== 200) {
    throwError(
      new Error(response.statusText || `Error: Received status code ${response.status}`),
      response
    );
  }
  const cart = response.data;
  const cartSegment = {
    ...cart,
    ...cart[segment]
  };

  if (cartSegment && cartSegment.ok === false) {
    throwError(
      new Error(cart.reason || 'Cart update error'),
      response
    );
  }

  if (response && response.error) {
    throwError(
      new Error('Cart update error'),
      response
    );
  }

  if (!cartSegment.displayedInfo) {
    throwError(
      new Error('Cart response missing displayedInfo'),
      response
    );
  }

  if (!cartSegment.orderItems) {
    throwError(
      new Error('Cart response missing orderItems'),
      response
    );
  }

  return cartSegment;
};

export const fetchShoppingCart = ({ segment = 'merchandise', userId = '' } = {}) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/shopping_cart/current${userId ? `?userId=${userId}` : ''}`;
  const raw = true;
  const addlHeaders = { Accept: 'application/json; version=v3' };
  return request.get(url, {}, raw, addlHeaders).then(response => processCartResponse(response, { segment }));
};

export const updateShoppingCart = (data, { segment = 'merchandise' } = {}) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/shopping_cart/current`;
  const raw = true;
  const addlHeaders = { Accept: 'application/json; version=v3' };
  return request.post(url, data, raw, () => {}, addlHeaders)
    .then(response => processCartResponse(response, { segment }));
};

export const reviewShoppingCartProducts = (data) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/shopping_cart/products?action=review`;
  const raw = true;
  const addlHeaders = { Accept: 'application/json; version=v3' };
  const segment = 'merchandise';
  return request.post(url, data, raw, () => {}, addlHeaders).then(response => processCartResponse(response, { segment }));
};

export const addProductToCart = (data) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/shopping_cart/current?action=add`;
  const raw = true;
  const addlHeaders = { Accept: 'application/json; version=v3' };
  const segment = 'merchandise';
  return request.post(url, data, raw, () => {}, addlHeaders).then(response => processCartResponse(response, { segment }));
};

export const removeProductFromCart = (data) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/shopping_cart/current?action=remove`;
  const raw = true;
  const addlHeaders = { Accept: 'application/json; version=v3' };
  const segment = 'merchandise';
  return request.post(url, data, raw, () => {}, addlHeaders).then(response => processCartResponse(response, { segment }));
};

const processAddressValidationResponse = (response) => {
  if (response.status !== 200) {
    throwError(
      new Error((response.error && response.error.reason) || `Error: Received status code ${response.status}`),
      response
    );
  }
  const validAddress = response.data;

  if (validAddress && validAddress.ok === false && (validAddress.error_code || validAddress.error_message)) {
    throwError(
      new Error(validAddress.error_message || validAddress.reason || 'Address validation error'),
      response
    );
  }

  return validAddress;
};

export const verifyPaymentAddress = (data) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/validate_address`;
  const raw = true;
  return request.post(url, data, raw)
    .then(response => processAddressValidationResponse(response));
};

/**
 * Fetches the payment metadata from server.
 * This includes stripe token and package pricing.
 * We only need this once per session so cache response.
 */
let _paymentMetadata = null;

const fetchPaymentMetadata = () => {
  // Returned cached metadata if available.
  if (_paymentMetadata) {
    return Promise.resolve(_paymentMetadata);
  }

  // Fetch metadata from warehouse.
  const url = `${WAREHOUSE_API_URL}/api/payment/get_metadata`;
  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);

      const metadata = get(response, 'data', null);
      if (!metadata) {
        throw new Error('No stripe token');
      }

      // Cache metadata and return.
      _paymentMetadata = metadata;
      return _paymentMetadata;
    });
};

/**
 * Fetches the service package metadata.
 * We only need this once per session so cache response.
 */
let _packageMetaData = null;
export const fetchPackages = () => {
  // Returned cached package metadata if available.
  if (_packageMetaData) {
    return Promise.resolve(_packageMetaData);
  }

  // Fetch package from warehouse.
  return fetchPaymentMetadata()
    .then(metadata => {
      const packages = get(metadata, 'packages', null);
      if (!packages) {
        throwError(
          new Error('No package data'),
          metadata
        );
      }

      // Cache package and return.
      _packageMetaData = packages;
      return _packageMetaData;
    });
};

/**
 * Fetches the Stripe API token.
 * We only need this once per session so cache response.
 */
let _stripeApiToken = null;
export const fetchStripeApiToken = () => {
  // Returned cached token if available.
  if (_stripeApiToken) {
    return Promise.resolve(_stripeApiToken);
  }

  // Fetch token from warehouse.
  return fetchPaymentMetadata()
    .then(metadata => {
      const token = get(metadata, 'meta.token', null);
      if (!token) {
        throwError(
          new Error('No stripe token'),
          metadata
        );
      }

      // Cache token and return.
      _stripeApiToken = token;
      return _stripeApiToken;
    });
};

/**
 * Place Order with warehouse back-end.
 */
export const placeOrder = (params) => {
  const url = `${WAREHOUSE_API_URL}/api/payment/place_order`;
  const raw = false;
  const addlHeaders = { Accept: 'application/json; version=v3' };
  return request.post(url, params, raw, () => {}, addlHeaders)
    .then(response => {
      const order = response.data;

      if (order && order.ok === false) {
        throwError(
          new Error(
            order.error_message ||
            order.errorMessage ||
            order.reason ||
            'Order failed. Please try again.'
          ), {
            url,
            order
          }
        );
      }

      if ((response && response.error) || !order) {
        throwError(
          new Error('Order failed. Please try placing order again.'), // network error?
          {
            url,
            params,
            response
          }
        );
      }

      return order;
    });
};

/**
 * Product Alternatives search API
 * Based on the search param it will switch between PAC search & pure alternative search.
 * This is a paginated response with optional filters.
 */
export const getProductAlternatives = ({
  productId,
  searchValue = '',
  pageNum,
  overrideDefaultProductType,
  queryParams = ''
}) => {
  let url = '';

  if (searchValue) {
    /**
     * Product Alternative Catalog (PAC) search API.
     * Note the absence of mode=alternatives and the addition of a search param.
     */
    url = (
      `${PRODUCT_SEARCH_API_URL}/warehouse/api/products` +
      `?app=studio&format=json&page=${pageNum}${queryParams}&id=${productId}&search=${searchValue}&page_size=40`
    );
  } else {
    /**
     * Product Alternatives search API.
     * Note the presence of mode=alternatives and the absence of search param.
     */
    url = (
      `${PRODUCT_SEARCH_API_URL}/warehouse/api/products` +
      `?id=${productId}&app=studio&mode=alternatives&format=json&page=${pageNum}${queryParams}&page_size=40`
    );
  }

  url += '&exclude_original_product=true';

  if (overrideDefaultProductType) {
    url += '&exclude_original_product_type=true';
  }

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Get product add-ons.
 */
export const getProductAddOns = ({
  productId
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/search/addons?page_size=2&product_id=${productId}`;

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * load design without using a component
 */
export const loadActiveSpaceDesign = (designId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}/shared?filter=full`;

  return request.get(url)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Live Swap Replace API.
 */
export const swapProduct = ({
  designId,
  hotspotInstanceUids,
  oldProductId,
  newProductId,
  snapshotId
}) => {
  if (!Array.isArray(hotspotInstanceUids) || hotspotInstanceUids.length <= 0) {
    console.error('hotspotInstanceUids must be an array of instanceUuids');
    return;
  }
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}/designsnapshots`;
  let postData = {
    replace_info: {},
    snapshot_id: snapshotId || null
  };

  // set product id for all hotspot Uuids
  for (let hotspotUuid of hotspotInstanceUids) {
    postData.replace_info[hotspotUuid] = {
      old_product_id: oldProductId,
      new_product_id: newProductId
    };
  }

  return request.post(url, postData, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Live Swap Revision History API V2.
 */
export const revisionHistory = ({
  designId,
  shared
}) => {
  const raw = true;
  const limit = 10; // currently limited to 10 revisions for performance reasons.
  const baseUrlPath = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}`;
  const method = shared ? 'designsnapshots_shared' : 'designsnapshots';
  const url = `${baseUrlPath}/${method}?limit=${limit}&full_renderings=true`;

  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Get Designs by Ids
 */
export const getDesignsByIds = ({
  ids = [],
  isSharedSpace = false
} = {}) => {
  if (!ids || ids.length === 0) {
    return [];
  }
  const idList = ids.join(',');
  const raw = true;

  const url = isSharedSpace
    ? ApiConfig.SHARED_DESIGNS_BY_IDS_V2(idList)
    : ApiConfig.DESIGNS_BY_IDS_V2(idList);

  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Get Design API.
 */
export const getDesign = (designId) => {
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}?filter=full`;

  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Get Design Brief API.
 */
export const getDesignBrief = (designId) => {
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}`;

  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

/**
 * Live Swap GET Render API.
 */
export const getRender = ({
  renderUuid,
  attempt
}) => {
  const raw = true;
  let url = `${WAREHOUSE_API_URL}/warehouse/api/v2/renderings/${renderUuid}`;

  if (attempt) {
    url += `?attempt=${attempt}`;
  }

  return request.get(url, {}, raw)
    .then(response => {
      // 409 conflict errors are expected and mean that the
      // render was requested before the prepare phase is completed.
      if (response.status === 409) {
        console.log(response); // intentionally leaving in for now

        // Check the error property.
        //
        // If the error property exists, is a string, and includes
        // the text "fail" or "not found", then the replacement or
        // prepare failed and the render is never going to get created.
        //
        // Otherwise, assume the prepare phase is still in progress.
        let {
          error
        } = response;
        if (error.error) {
          error = error.error;
        }
        if (
          error && typeof error === 'string' &&
          (error.includes('fail') || error.includes('not found'))
        ) {
          throwError(
            new Error(`${error} For render uuid: ${renderUuid}.`),
            url
          );
        }

        return {
          status: 'preparing'
        };
      }

      // Handle errors.
      if (response.error) {
        console.log(response); // intentionally leaving in for now
        throwOnResponseError(response, url);
      }

      return response.data;
    });
};

/**
 * Batch get MRS renders API.
 */
export const batchGetRenders = ({
  renderingUuids,
  attempt
}) => {
  const raw = true;
  let url = `${WAREHOUSE_API_URL}/warehouse/api/v2/renderings/batch?id_list=${renderingUuids.join(',')}`;

  if (attempt) {
    url += `&attempt=${attempt}`;
  }

  return request.get(url, {}, raw)
    .then(response => {
      return response.data;
    });
};

/**
 * Email Verification API.
 */
export const verifyEmail = ({
  token
}) => {
  const url = `${WAREHOUSE_API_URL}/account/email-verification/${token}`;

  return request.get(url)
    .then(response => {
      if (!response || response.error) {
        throwError(
          new Error('Invalid Email Verification Token'),
          url
        );
      }

      return response.data;
    });
};

/**
 * Abandon a design.
 * This updates the design status to abandoned on server.
 */
export const abandonDesign = (designId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}/abandon`;
  const raw = true;
  const patchData = {};

  return request.patch(url, patchData, raw).then(response => {
    if (response.status !== 200) {
      const errMsg = get(response, 'error.detail', '');
      throwError(
        new Error(`Error while trying to abandon design ${designId}: ${errMsg}`),
        url
      );
    }
  });
};

/**
 * Undo / Redo / Cancel design revision.
 * This updates the current design revision on the server.
 */
export const undoRevision = (designId) => {
  return revisionUndoRedoCancel(designId, 'undo');
};

export const redoRevision = (designId) => {
  return revisionUndoRedoCancel(designId, 'redo');
};

export const cancelRevision = (designId) => {
  return revisionUndoRedoCancel(designId, 'pop');
};

const revisionUndoRedoCancel = (designId, action) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}/current_designsnapshot`;
  const raw = true;
  const patchData = {
    action
  };

  return request.patch(url, patchData, raw).then(response => {
    if (response.status !== 200) {
      throwError(
        new Error(`${action} revision failed with status ${response.status} for design ${designId}`),
        url
      );
    }

    return response.data;
  });
};

/**
 * Fetches all user favorites
 */
export const fetchAllUserFavorites = (page = 1) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/favorites?page=${page}`;

  const raw = true;
  return request.get(url, {}, raw).then(response => {
    if (response.status !== 200) {
      throwError(
        new Error(`Retrieval of like statuses for user failed with status ${response.status}`),
        url
      );
    }

    return response.data;
  });
};

export const updateProductLikeStatus = ({
  productId,
  likeStatus,
  reasons,
  source
}) => {
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/warehouse/api/favorites`;
  const productData = {
    id: productId,
    like: likeStatus
  };

  if (reasons) {
    productData.reasons = reasons;
  }
  if (source) {
    productData.source = source;
  }

  const postData = {
    products: [productData]
  };
  return request.post(url, postData, raw).then(response => {
    if (response.status !== 200) {
      throwError(
        new Error(`Update of like status for product ${productId} failed with status ${response.status}`),
        url
      );
    }

    return response.data;
  });
};

/**
 * Bulk update product like status.
 * This allows multiple updates in a single API request.
 * @param {Array} products - Array of products to update.
 *
 * Each product object should include a 'like' field set to one of: [-1, 0, 1]
 * Each product object may optionally include a 'reasons' field.
 */
export const bulkUpdateProductLikeStatus = (products) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/favorites`;

  const productData = products.map(prod => {
    let likeData = {
      id: prod.id,
      like: prod.like
    };
    if (prod.reasons) {
      likeData.reasons = prod.reasons;
    }
    return likeData;
  });

  const postData = {
    products: productData
  };

  const raw = true;
  return request.post(url, postData, raw).then(response => {
    if (response.status !== 200) {
      throwError(
        new Error(`Update of like status for ${JSON.stringify(postData)} failed with status ${response.status}`),
        url
      );
    }

    return response.data;
  });
};

export const regenerateDesign = spaceId => {
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/showroom/api/showrooms/${spaceId}?showroom=true&start_styling=true`;

  return request.patch(url, null, raw).then(response => {
    if (response.status !== 200) {
      throwError(
        new Error(`Regeneration of design for space ${spaceId} failed with a status of ${response.status}`),
        url
      );
    }

    return response.data;
  });
};

/**
 * Sign up
 */
export const signUp = ({
  email,
  password,
  referral
}) => {
  const url = `${WAREHOUSE_API_URL}/api/users/signup`;

  let options = {
    login_if_already_signed_up: true,
    user: {
      email,
      password,
      referral_answer: (referral && referral.value) || '',
      firstName: '',
      lastName: ''
    }
  };

  const utmString = cookie.get('utm_string');
  if (utmString) {
    options.user.utmString = utmString;
  }

  return request.post(url, options).then(response => {
    // Check for server errors.
    if (
      response.error ||
      (response.data && response.data.ok !== undefined && !response.data.ok) ||
      (response.data && !response.data.email)
    ) {
      let error = 'Server Error!';
      if (response.data && response.data.reason) {
        error = response.data.reason;
      }

      throwError(new Error(error), {
        url,
        options,
        response
      });
    }

    return response.data; // SUCCESS!
  });
};

/**
 * Passwordless sign up
 */
export const signUpPasswordless = ({
  email,
  referral
}) => {
  const url = `${WAREHOUSE_API_URL}/api/users/signup`;

  let options = {
    login_if_already_signed_up: true,
    user: {
      email,
      referral_answer: (referral && referral.value) || '',
      firstName: '',
      lastName: ''
    },
    skip_password: true
  };

  const utmString = cookie.get('utm_string');
  if (utmString) {
    options.user.utmString = utmString;
  }

  return request.post(url, options).then(response => {
    // Check for server errors.
    if (
      response.error ||
      (response.data && response.data.ok !== undefined && !response.data.ok) ||
      (response.data && !response.data.email)
    ) {
      let error = 'Server Error!';
      if (response.data && response.data.reason) {
        error = response.data.reason;
      }

      throwError(new Error(error), {
        url,
        options,
        response
      });
    }

    return response.data; // SUCCESS!
  });
};

/**
 * Login
 */
export const login = ({
  email,
  password
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/login`;

  let options = {
    username: email,
    password
  };

  return request.post(url, options).then(response => {
    if (
      response.error ||
      (response.data && response.data.ok !== undefined && !response.data.ok) ||
      (response.data && !response.data.email)
    ) {
      let error = 'Server Error!';
      if (response.data && response.data.reason) {
        error = response.data.reason;
      } else if (response.status === 400) {
        error = 'Bad Request';
      } else if (response.status === 401) {
        error = 'User not authorized';
      } else if (response.status === 402) {
        error = 'Designs not found';
      } else if (response.status === 403) {
        error = 'Login error. Please try again';
      } else if (response.status === 404) {
        error = 'Invalid Username/password';
      }

      throwError(new Error(error), {
        url,
        options,
        response
      });
    }

    return response.data; // SUCCESS!
  });
};

/**
 * Reset password
 */
export const resetPassword = ({
  email = '',
  next = ''
} = {}) => {
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/warehouse/api/user/reset-password${next ? `?next=${next}` : ''}`;
  return request.post(url, { email }, raw).then(response => {
    if (response.error || response.status !== 200) {
      throwError(new Error(response.error || 'No account associated with email'), {
        url,
        email,
        response
      });
    }

    return true; // SUCCESS!
  });
};

/**
 * Change password
 */
export const changePassword = ({
  password = '',
  token = ''
} = {}) => {
  const raw = true;
  const url = `${WAREHOUSE_API_URL}/warehouse/api/user/change-reset`;
  return request.post(url, { password, token }, raw).then(response => {
    if (response.error || response.status !== 200) {
      throwError(new Error(response.error || 'Password change failed. Please try again'), {
        url,
        token
      });
    }

    return response.data; // SUCCESS!
  });
};

/**
 * Gets user info from BE including FFs
 */
export const getUserInfo = () => {
  const url = `${WAREHOUSE_API_URL}/api/users/info`;
  const raw = true;
  return request.get(url, null, raw).then(response => {
    if (response.error || response.status !== 200) {
      throwError(new Error(response.error || 'GET user info failed.'), {
        url
      });
    }

    return {...response.data, success: true};
  });
};

/**
 * Fetches the customer info.
 * Customer = non logged in user.
 * Used when a user, logged in as an Admin,
 * is viewing a Customer studio.
 */
export const getCustomerInfoFrom = (userId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/users/${userId}`;
  const raw = true;
  return request.get(url, null, raw).then(response => {
    if (response.error || response.status !== 200) {
      throwError(new Error(response.error || 'GET customer info from userId failed.'), {
        url
      });
    }

    return {...response.data, success: true};
  });
};

/**
 * Update the user's profile (settings)
 */
export const updateUserProfile = (data, userId = 0) => {
  const userIdQueryParam = userId ? `?user_id=${userId}` : '';
  const url = `${WAREHOUSE_API_URL}/warehouse/api/user/change_profile${userIdQueryParam}`;
  const raw = true;
  return request.post(url, data, raw).then(response => {
    if (response.error || response.status !== 200) {
      throwError(new Error(response.error || 'POST user profile failed.'), {
        url
      });
    }

    return {...response.data, success: true};
  });
};

/**
 * Creates a modsy showroom package.
 */
export const createShowroom = async () => {
  const url = `${WAREHOUSE_API_URL}/showroom/api/showrooms`;
  let data = {};
  const raw = true;
  let response = await request.post(url, data, raw);
  console.log(`POST ${url}`, response.status, response.data || response.error); // intentionally leaving in for now

  if (response.status !== 200) {
    const msg = get(response, 'error.error', `request failed with status ${response.status}`);
    throwError(new Error(msg), url);
  }
  const spaceId = get(response, 'data.showroom_container.space_id');
  const designId = get(response, 'data.showroom_container.design_id');
  const styleboardInfoId = get(response, 'data.showroom_container.styleboard_info_id');
  if (!spaceId) {
    throwError(new Error('Create showroom response missing space_id'), url);
  } else if (!designId) {
    throwError(new Error('Create showroom response missing design_id'), url);
  } else if (!styleboardInfoId) {
    throwError(new Error('Create showroom response missing styleboard_info_id'), url);
  }

  return {
    spaceId,
    designId,
    styleboardInfoId
  };
};

/**
 * Updates a modsy showroom package via PATCH request.
 * Will only update truthy values passed in.
 * Will also kick off re-styling if startRestyling flag is passed.
 */
export const patchShowroom = async ({
  colors, // [String]
  floorType, // String
  layoutOption, // String
  lookingForOption, // String
  priceRange, // String
  purchaseTimingOption, // String
  roomType, // Integer
  roomFunctions, // [String]
  spaceId, // Integer
  styleGenome, // String
  styleQuizAttempts, // Number
  styleQuizPureStyleScores, // [Object(name, score)]
  templateOption, // String
  wallColor, // String

  startStyling // If true, PATCH request will also kick off restyling
}) => {
  const url = `${WAREHOUSE_API_URL}/showroom/api/showrooms/${spaceId}${startStyling ? '?start_styling=true' : ''}`;
  let data = {
    room_config: {
      floor_style: floorType, // String
      wall_color: wallColor // String
    },
    room_seed: {
      base_colors: colors || 'neutrals', // [String] -- required
      budget: priceRange || 'mid-range', // String -- required
      help_needed: lookingForOption, // String
      num_stylequizes: styleQuizAttempts, // Number
      purchase_timing: purchaseTimingOption, // String
      room_functions: roomFunctions, // [String] -- can be empty array
      room_model: layoutOption, // String
      room_template_group: templateOption, // String
      room_type: roomType, // Integer
      style_genome: styleGenome // String
    }
  };

  if (styleQuizPureStyleScores && styleQuizPureStyleScores.length > 0) {
    data.room_seed.pure_styles = styleQuizPureStyleScores.slice(0, 3); // keep the top 3 pure styles for analytics
  }

  const raw = true;
  const response = await request.patch(url, data, raw);
  console.log(`PATCH ${url}`, response.status, response.data || response.error);

  if (response.status !== 200) {
    const msg = get(response, 'error.error', `request failed with status ${response.status}`);
    throwError(new Error(msg), {
      url,
      data,
      response
    });
  }
  const designsnapshot = get(response, 'data.designsnapshot');
  if (!designsnapshot) {
    throwError(new Error('PATCH showroom response missing designsnapshot'), {
      url,
      data,
      response
    });
  } else if (designsnapshot.error) {
    const error = designsnapshot.error;
    throwError(new Error(`PATCH showroom designsnapshot failed with error: ${error}`), {
      url,
      data,
      response
    });
  }

  return designsnapshot;
};

/**
 * Kicks off showroom creation.
 */
export const launchShowroom = async ({
  progressCb,
  showroom
}) => {
  // Validate showroom
  if (!showroom) {
    throwError(new Error('Showroom is missing'));
  }

  const roomType = showroom.roomType();
  if (!roomType) {
    throwError(new Error('Showroom is missing room type'), JSON.stringify(showroom));
  }

  const styleGenome = showroom.styleGenome();
  if (!styleGenome) {
    throwError(new Error('Showroom is missing style genome'), JSON.stringify(showroom));
  }

  const layoutOption = showroom.layoutOption();
  if (!layoutOption) {
    throwError(new Error('Showroom is missing layout option'), JSON.stringify(showroom));
  }

  const floorType = showroom.floorOption();
  if (!floorType) {
    throwError(new Error('Showroom is missing floor option'), JSON.stringify(showroom));
  }

  const wallColor = showroom.wallOption();
  if (!wallColor) {
    throwError(new Error('Showroom is missing wall color option'), JSON.stringify(showroom));
  }

  const roomFunctions = showroom.roomFunctions();
  if (!roomFunctions) {
    throwError(new Error('Showroom is missing room functions'), JSON.stringify(showroom));
  }

  const templateOption = showroom.templateOption();
  if (!templateOption) {
    throwError(new Error('Showroom is missing room template'), JSON.stringify(showroom));
  }

  const spaceId = showroom.spaceId();
  if (!spaceId) {
    throwError(new Error('Showroom is missing space id'), JSON.stringify(showroom));
  }

  // Update showroom with room_type.
  // This kicks off the automated styling.
  progressCb('generating showroom studio');
  await patchShowroom({
    colors: showroom.colors(),
    floorType,
    layoutOption,
    lookingForOption: showroom.lookingForOption(),
    priceRange: showroom.priceRange(),
    purchaseTimingOption: showroom.purchaseTimingOption(),
    roomFunctions,
    roomType,
    spaceId,
    styleGenome,
    styleQuizAttempts: showroom.styleQuizAttempts(),
    styleQuizPureStyleScores: showroom.styleQuizPureStyleScores(),
    templateOption,
    wallColor,
    startStyling: true
  });
};

/**
 * Retrieves the onboarding metadata for a showroom space.
 */
export const getShowroomMetadata = (showroomSpaceId) => {
  const url = `${WAREHOUSE_API_URL}/showroom/api/showrooms/${showroomSpaceId}`;
  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      if (!response || response.error) {
        const errMsg = (response && response.error) || 'Unknown error';
        throwError(
          new Error(`Fetching metadata for showroom ${showroomSpaceId} failed: ${JSON.stringify(errMsg)}`),
          url
        );
      }

      return response.data;
    });
};

/**
 * Fetches style genome metadata.
 */
export const getStyleGenomeMetadata = (genomeName) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/seasonal-genomes?name=${genomeName}`;
  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      if (!response || response.error) {
        const errMsg = (response && response.error) || 'Unknown error';
        throwError(
          new Error(`Fetching metadata for ${genomeName} failed: ${JSON.stringify(errMsg)}`),
          url
        );
      }

      return response.data;
    });
};

/**
 * Creates a new space/styleboard with styleboard_info
 */
export const createSpace = (spaceData) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/create_styleboard`;
  const postData = {
    space_info: spaceData
  };

  const raw = true;
  return request.post(url, postData, raw)
    .then(response => {
      const createdSpace = get(response, 'data.results[1]', null);

      if (
        !response ||
        response.error ||
        !createdSpace
      ) {
        const errMsg = (response && response.error) || 'Unknown error';
        throwError(
          new Error(`Creating space failed: ${JSON.stringify(errMsg)}`), {
            url,
            postData
          }
        );
      }

      return createdSpace;
    });
};

/**
 * Creates a new space/styleboard with styleboardInfo from a showroom space
 */
export const createSpaceFromShowroomSpace = (showroomSpaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/create_styleboard?showroom=${showroomSpaceId}`;

  const raw = true;

  return request.post(url, {}, raw)
    .then(response => {
      const createdSpace = get(response, 'data.results[1]', null);

      if (
        !response ||
        response.error ||
        !createdSpace
      ) {
        const errMsg = (response && response.error) || 'Unknown error';
        throwError(
          new Error(`Creating space failed: ${JSON.stringify(errMsg)}`),
          url
        );
      }

      return createdSpace;
    });
};

/**
 * get array of product infos by passing array of product ids
 */
export const getProductsByIds = ids => {
  // don't request products if we have no ids
  if (!ids || ids.length === 0) {
    return [];
  }

  const idList = ids.join(',');
  const url = `${WAREHOUSE_API_URL}/warehouse/api/products/batch?id_list=${idList}`;
  const raw = true;

  return request.get(url, {}, raw)
    .then(response => {
      throwOnResponseError(response, url);

      let products = get(response, 'data', []);
      return products;
    });
};

/**
 * Returns the most recent (latest) pre-purchase space for a user.
 */
export const getLatestPrePurchaseSpace = () => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/latest`;

  const raw = true;
  return request.get(url, {}, raw)
    .then(response => {
      // Handle case where this user does not have
      // any pre-purchase spaces.
      if (response && response.status === 404) {
        throwError(
          new Error('Pre-purchase space not found'),
          url
        );
      }

      // Handle unknown errors.
      throwOnResponseError(response, url);

      let space = get(response, 'data.result', {});
      if (!space || !space.id || !space.space_id) {
        throwError(
          new Error('Pre-purchase space not found'),
          url
        );
      }

      return space;
    });
};

// get the user from the space
// necessary for admin-as-user
export const getSpaceUser = memoizeAsync(async ({spaceId, isAdminUser}) => {
  if (spaceId && isAdminUser) {
    let space = await getSpace({spaceId});

    return space.user;
  }

  return null;
});

export const submitAboutYou = ({
  userId,
  aboutYouText = '',
  whoLivesHereOptions = {},
  whoLivesHereAdditional = '',
  phoneNumber = '',
  phoneTextingEnabled = false

}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/about_users`;

  const data = {
    user: userId,
    about_you: aboutYouText,
    additional_info: whoLivesHereAdditional,
    in_home_adults: whoLivesHereOptions.adults,
    in_home_teens: whoLivesHereOptions.teens,
    in_home_big_kids: whoLivesHereOptions.bigKids,
    in_home_little_kids: whoLivesHereOptions.littleKids,
    in_home_babies: whoLivesHereOptions.babies,
    in_home_cats: whoLivesHereOptions.cats,
    in_home_dogs: whoLivesHereOptions.dogs,
    in_home_other_pets: whoLivesHereOptions.otherPets,
    phone: phoneNumber,
    texting_enabled: phoneTextingEnabled
  };

  return request.post(url, data)
    .then(response => {
      throwOnResponseError(response, url);
      return response.data;
    });
};

export const updateAboutYou = ({
  userId,
  aboutYouText = '',
  whoLivesHereOptions = {},
  whoLivesHereAdditional = '',
  phoneNumber = '',
  phoneTextingEnabled = false
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/about_users/${userId}`;
  const raw = true;
  const data = {
    user: userId,
    about_you: aboutYouText,
    additional_info: whoLivesHereAdditional,
    in_home_adults: whoLivesHereOptions.adults,
    in_home_teens: whoLivesHereOptions.teens,
    in_home_big_kids: whoLivesHereOptions.bigKids,
    in_home_little_kids: whoLivesHereOptions.littleKids,
    in_home_babies: whoLivesHereOptions.babies,
    in_home_cats: whoLivesHereOptions.cats,
    in_home_dogs: whoLivesHereOptions.dogs,
    in_home_other_pets: whoLivesHereOptions.otherPets,
    phone: phoneNumber,
    texting_enabled: phoneTextingEnabled
  };

  return request.patch(url, data, raw).then(response => {
    if (response.status !== 200) {
      throwError(
        new Error(`Update failed for about_users for user ${userId}`),
        url
      );
    }

    return response.data;
  });
};

export const getAboutYou = (userId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/about_users/${userId}`;
  const raw = true;

  // If the logged-in user is admin then the API will return
  // info about any user given by userId
  // if not it will only return data about the logged-in user

  // Expected response format
  // {
  //   about_your_space: <string>,
  //   additional_space_info: <string>,
  //   who_lives_here: {
  //            adults: <int>,
  //            teens: <int>,
  //            big_kids: <int>,
  //            little_kids: <int>,
  //            babies: <int>,
  //            dogs: <int>,
  //            cats: <int>,
  //            other_pets: <int>
  //            }
  //  phone: <string>
  //  texting_enabled: <boolean>
  // }

  return request.get(url, null, raw).then(response => {
    // endpoint will 404 if the user's about you info is not found
    if (response.status === 404) {
      return null;
    }

    throwOnResponseError(response);

    return response.data;
  });
};

/** * * * * * * * * * * * * * *
* PPO METHODS
* * * * * * * * * * * * * * * */

export const getStyleboardInfo = (styleboardId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/styleboardinfo?space_id=${styleboardId}&ppo=1`;
  const raw = false;

  return request.get(url, null, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const getMobilePPOInfo = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/mobile_ppo_dashboard/${spaceId}`;
  const raw = true;

  return request.get(url, null, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const getPPOProductCategories = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/product-categories?spaceId=${spaceId}`;
  const raw = false;

  return request.get(url, null, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const patchStyleboardInfo = ({ styleboardInfo, allowEmpty = false }) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/styleboardinfo/${styleboardInfo.id}?ppo=1`;
  const raw = false;

  if (allowEmpty) {
    return request.patchAllowEmpty(url, styleboardInfo, raw).then(response => {
      if (response.error) {
        throwOnResponseError(response, url);
      }
      return response.data;
    });
  }

  return request.patch(url, styleboardInfo, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const patchStyleboard = (styleboardId, data) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/styleboards/${styleboardId}`;
  const raw = false;

  return request.patch(url, data, raw)
    .then(response => {
      if (response.error) {
        throwOnResponseError(response, url);
      }
      return response.data;
    });
};

export const getSpaceData = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}`;
  const raw = false;

  return request.get(url, null, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const submitSpacePhotos = (spaceId, photoData) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/dashboard/submit-photos`;
  const raw = false;

  return request.post(url, photoData, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return {
      success: !response.error,
      error: response.error
    };
  });
};

// method used in the Experimental PPO
export const submitSpacePhoto = async (spaceId, photoData,
  onUploadProgressCallback = () => { },
  onUpdatePhotoIdCallback) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/dashboard/submit-photos`;
  const raw = false;
  const response = await request.post(url, photoData, raw, onUploadProgressCallback).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    if (onUpdatePhotoIdCallback && response.data.photoIds) {
      const photoId = response.data.photoIds[0];
      onUpdatePhotoIdCallback(photoId);
    }
    return {
      success: !response.error,
      error: response.error
    };
  });
  return response;
};

export const getPhotosAndMeasurementData = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/dimension_dataset`;
  const data = { params: { space_id: spaceId } };
  const raw = false;

  return request.get(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data.results[0];
  });
};

export const submitMeasurementData = (spaceId, data) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/dashboard/submit-measurement`;
  const raw = true;

  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const submitFloorPlan = (spaceId, data) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/dashboard/submit-photos`;
  const raw = false;

  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

// method used in the Experimental PPO
export const submitFloorPlanExperimental = async (spaceId, floorPlanPhotoData,
  onUploadProgressCallback = () => { },
  onUpdatePhotoIdCallback) => {
  return submitSpacePhoto(spaceId, floorPlanPhotoData, onUploadProgressCallback, onUpdatePhotoIdCallback);
};

export const submitSpace = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/styleboards/${spaceId}/transition_to_under_review`;
  const raw = false;

  return request.patch(url, null, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const getOrders = ({
  page = 1,
  pageSize = 20
} = {}) => {
  const url = ApiConfig.GET_ORDERS();

  const data = {
    params: {
      page: isInteger(page) ? page : 1,
      page_size: isInteger(pageSize) ? pageSize : 20
    }
  };

  const raw = true;

  return request.get(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/** Layout selection */
export const selectLayout = ({
  spaceId,
  layoutDesignId,
  userNotes,
  selected = true
} = {}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/v2/spaces/${spaceId}/layouts`;

  const data = {
    selected,
    design_pk: layoutDesignId,
    customer_notes: userNotes
  };

  const raw = true;

  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

export const getProductCategories = ({
  categoryPathUrl = '',
  nodeType = ''
} = {}) => {
  const url = ApiConfig.GET_PRODUCTCATEGORIES();

  const data = {
    params: {
      path_url: categoryPathUrl,
      node_type: nodeType
    }
  };

  const raw = true;

  return request.get(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/** Update contact info **/
export const updateContactInfo = ({
  phone = '',
  textingEnabled = false
} = {}) => {
  const url = ApiConfig.UPDATE_CONTACT_INFO();

  const data = {
    phone,
    texting_enabled: textingEnabled
  };

  const raw = true;

  return request.patch(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

// =============================================
// Style Validation (new PPO style quiz) methods
// =============================================

export const getStyleQuizRooms = ({
  spaceId = 0,
  page = 0
}) => {
  const url = `${WAREHOUSE_API_URL}/quiz/api/rooms/${spaceId}/${page}?full_image_data=true`;
  return request.get(url).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const getStyleQuizRoomLikeReasons = () => {
  const url = `${WAREHOUSE_API_URL}/quiz/api/likereasons?ordered=true`;
  return request.get(url).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

export const putStyleQuizRoomLike = ({
  spaceId = 0,
  roomId = 0,
  rating = 0,
  reasons = [],
  source = 1, // source id 1 maps to PPO
  productId = 0,
  instanceId = '0000-xxxx-1111-yyyy'
}, type) => {
  const url = `${WAREHOUSE_API_URL}/feed/api/collections/likes?space_id=${spaceId}`;
  const data = {
    room_id: roomId,
    rating,
    reasons: reasons || [],
    source
  };
  if (type === 'liked-product') {
    data.product_id = productId;
    data.instance_id = instanceId;
  }
  const raw = true;

  return request.put(url, data, raw).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    // The returned response is now a list of dict
    return response.data[0];
  });
};

export const setStyleQuizComplete = ({
  spaceId = 0,
  source = 1 // source id 1 maps to PPO
}) => {
  const url = `${WAREHOUSE_API_URL}/feed/api/collections/complete?space_id=${spaceId}`;

  const data = {
    source
  };
  const raw = true;

  return request.post(url, data, raw).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

export const getStyleQuizComplete = ({
  spaceId = 0
}) => {
  const url = `${WAREHOUSE_API_URL}/feed/api/collections/complete?space_id=${spaceId}`;

  return request.get(url).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data && response.data.complete;
  });
};

export const getStyleQuizResults = ({
  spaceId = 0
}) => {
  const url = `${WAREHOUSE_API_URL}/feed/api/collections?space_id=${spaceId}&style=quiz&full_image_data=true`;
  return request.get(url).then((response) => {
    // 404 response are expected if user has not yet taken SQv2 for this space.
    if (response.status === 404) {
      return response;
    }

    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const setStyleDescription = ({
  styleboardInfoId = 0,
  description = '',
  source = 1 // source id 1 maps to PPO
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/styleboardinfo/${styleboardInfoId}/update_style_description`;
  const data = {
    style_description: description,
    source
  };
  const raw = true;

  return request.post(url, data, raw).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/**
 * Returns the global (non-specific) SQv1 result for the provided userId.
 * If user has not taken SQv1, return null.
 */
export const getLegacyStyleQuizResult = ({
  spaceId = 0,
  userId = 0
}) => {
  const url = `${WAREHOUSE_API_URL}/api/survey_result?user_id=${userId}&space_id=${spaceId}`;
  return request.get(url).then((response) => {
    // A brand new user may not have taken style quiz.
    // In this case, API returns a 404 which makes sense.
    // We do not want to treat this scenario as an error.
    const userHasNotTakenSQv1 = response.status === 404;
    if (userHasNotTakenSQv1) {
      return null;
    }

    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

export const sendStaffMessage = ({
  message,
  subject
}) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/message-staff`;
  const data = {
    message,
    subject
  };

  return request.post(url, data).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

export const getPPOState = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/ppo-data?layout=web`;
  return request.get(url).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const getPPOData = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/ppo-data/room-details`;
  return request.get(url).then((response) => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

export const patchPPOData = (data, spaceId) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}/ppo-data`;
  const raw = false;

  return request.patch(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.questions;
  });
};

// =============================================
// Messenger methods
// =============================================

/**
 * Deletes a message - currently only can delete active redesign request flows
 * @param messageInfo: object requiring spaceId, messageId, and createdAt for the
 * message you want to delete
 * **you can just pass in the original message and the function
 * will extract the data the server needs
 */
export const deleteMessage = ({ messageInfo }) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/messages/delete`;
  const raw = false;
  const data = {
    created_at: messageInfo.createdAt,
    message_id: messageInfo.messageId,
    space_id: messageInfo.spaceId
  };
  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/**
 * Resets the unread message count for a conversation to zero.
 * @param conversationId: the conversation UUID
 */
export const clearUnreadMessageCount = (conversationId) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/conversations/${conversationId}/read`;
  return request.post(url)
    .then(() => {})
    .catch(error => bugsnagError(error, { conversationId }));
};

/**
 * Uploads images to warehouse and returns the new created message
 */
export const uploadMessagePhotos = (photoData) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/conversations/upload_images`;
  const raw = false;

  return request.post(url, photoData, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

export const createCsTicket = ({ spaceId, description, issueType, userId }) => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/user/${userId}/cs-ticket`;
  const raw = false;

  // The tag value is hardcoded as a BE requirement for the time being.
  const postData = {
    description,
    issue_type: issueType,
    space_id: spaceId,
    tags: ['messenger_thread_customer_contact']
  };

  return request.post(url, postData, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/**
 *  Fetches all macros for a given space and returns a flattened array
 */
export const getMacros = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/macros/${spaceId}`;

  return request.get(url).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    const result = macrosResolver({ response });
    return result;
  });
};

export const getAllConversationImages = (spaceId) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/conversations/images?space_id=${spaceId}`;

  return request.get(url).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    // Adds missing forward slash to the file paths coming from the server
    return conversationImagesReducer(response.data || []);
  });
};

/**
 * Adds missing forward slash to the image paths coming from the server
 */
const conversationImagesReducer = (response) => {
  return response.map(fileData => {
    const fileSrc = `/${fileData.image || fileData.s3Key}`;
    return {
      src: fileSrc,
      type: getFileType(fileSrc),
      id: `${fileData.s3_key ? 0 : 1}-${fileData.id}`
    };
  });
};

const getFileType = (fileSrc) => {
  const fileString = fileSrc || '';
  const fileParts = fileString.split('.');
  if (fileParts.length > 0) {
    const fileType = fileParts[fileParts.length - 1];
    switch (fileType.toLowerCase()) {
      case 'mp4':
      case 'm4a':
      case 'm4p':
      case 'm4r':
      case 'm4v':
      case 'mov':
        return 'video';
      case 'pdf':
        return 'pdf';
      default:
        return 'image';
    }
  }
  return 'image';
};

/**
 *  Fetches CS ticket issue types for use in Messenger
 */
export const getTicketIssueTypes = () => {
  const url = `${WAREHOUSE_API_URL}/warehouse/api/cs-ticket-issue-type`;

  return request.get(url).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response;
  });
};

/**
 * Retrieves conversation (member) info for each space in spaceIds
 * @param spaceIds: Array of objects integer space ids to retrieve
 */
export const getConversationsBatch = (spaceIds) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/conversations/batch`;
  const raw = false;
  const data = {
    spaces: spaceIds
  };

  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/**
 * Retrieves messages for each space in the spacesQueryInfo
 * @param spacesQueryInfo: Array of objects in the form --
 * [{
 *   spaceId: integer id for space,
 *   page: string identifier for next page (Optional)
 * }]
 * @param limit: Number of results per space
 */
export const getMessagesBatch = ({ spacesQueryInfo, limit }) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/messages/batch`;
  const raw = false;
  const data = {
    spaces: spacesQueryInfo.map(query => ({
      since: query.since,
      space_id: query.spaceId,
      page: query.page
    })),
    limit
  };

  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/**
 * Get a single message by spaceId and messageId from the server
 * Used to retrieve messages that we are waiting to update
 * @param spaceId: The id for the space where the message is
 * @param message: The id for the message
 */
export const getMessage = ({ spaceId, messageId }) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/space/${spaceId}/message/${messageId}`;
  return request.get(url).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }
    return response.data;
  });
};

/**
 * Sends a new message to be created on the server
 * @param spaceId: The id for the space the message should be added to
 * @param message: The string message content
 * @param startFlow: (Optional) the flow identifier for the automated flow to start
 */
export const sendNewMessage = ({ spaceId, images, message, startFlow }) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/messages`;
  const raw = false;
  const data = {
    space_id: spaceId,
    message
  };
  if (startFlow) {
    data.start_flow = startFlow;
  }
  if (images) {
    data.images = images;
  }

  return request.post(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

/**
 * Maps the option values to the correct parameters
 * for a message flow option in message update
 */
const mapMessageFlowOptionToSnakeCase = (option) => {
  const { label, imageSrc, value } = option;
  return {
    label,
    image: imageSrc,
    value
  };
};

/**
 * Array.map function to Convert all necessary camelCase keys to snake_case
 * for the message flow messages in message update
 */
const mapMessageFlowMessageToSnakeCase = (messageFlowMessage) => {
  const {
    detailText,
    message,
    messageOrder,
    messageType,
    options,
    response,
    senderId,
    skipReason
  } = messageFlowMessage;

  const newMessageFlow = {
    detail_text: detailText,
    message,
    message_order: messageOrder,
    message_type: messageType,
    options: options.map(mapMessageFlowOptionToSnakeCase),
    response,
    sender_id: senderId,
    skip_reason: skipReason
  };
  return newMessageFlow;
};

/**
 * Converts all necessary camelCase keys to snake_case
 * for the message flow in a message update
 */
const mapMessageFlowToSnakeCase = (messageFlow) => {
  const { complete, name, messages } = messageFlow;
  const newMessage = {
    complete,
    name,
    messages: messages.map(mapMessageFlowMessageToSnakeCase)
  };
  return newMessage;
};

/**
 * Converts all necessary camelCase keys to snake_case
 * for a message update
 */
const mapUpdateToSnakeCase = ({ messageData }) => {
  const newMessage = {
    conversation_id: messageData.conversationId,
    created_at: messageData.createdAt,
    message: messageData.message,
    message_id: messageData.messageId,
    space_id: messageData.spaceId,
    updated_at: messageData.updatedAt,
    user_id: messageData.userId
  };
  if (messageData.messageFlow) {
    newMessage.message_flow = mapMessageFlowToSnakeCase(messageData.messageFlow);
  }
  if (messageData.completeFlow) {
    newMessage.complete_flow = messageData.completeFlow;
  }
  return newMessage;
};

export const sendUpdateMessage = ({ message }) => {
  const url = `${WAREHOUSE_API_URL}/morpho/api/messages`;
  const raw = false;
  const data = mapUpdateToSnakeCase({ messageData: message });

  return request.patchAllowEmpty(url, data, raw).then(response => {
    if (response.error) {
      throwOnResponseError(response, url);
    }

    return response.data;
  });
};

// This formats macros data returned from the API
// These functions can be removed with the release of the Design Page
const macrosResolver = ({ response }) => {
  const id = 'base';
  const obj = {
    ...response.data,
    name: 'base'
  };

  return recurseReduceMacro({ accumulator: [], id, obj, parentId: '' });
};

const recurseReduceMacro = ({ accumulator, id, obj = {}, parentId }) => {
  if (obj === undefined || obj === null) {
    return [];
  }
  const children = Object.keys(obj)
    .filter(key => {
      return key !== 'content' && key !== 'name';
    }) || [];

  accumulator.push({
    children,
    content: obj.content,
    id,
    name: obj.name,
    parentId
  });

  children.forEach((child) => {
    recurseReduceMacro({
      accumulator,
      id: child,
      obj: obj[child],
      parentId: id
    });
  });

  return accumulator;
};
